"use client";

import React, { useRef } from "react";
import {
  animate,
  motion,
  useMotionValue,
  useReducedMotion,
} from "framer-motion";
import { useIsomorphicLayoutEffect } from "@/src/shared/hooks/useIsomorphicLayoutEffect";

interface IGuestFavouriteLaurelsProps {
  /** The badge's name ("Guest Favorite"). */
  label: string;
  /** Play the app's opening animation (phone); otherwise show the end state. */
  animated?: boolean;
}

/** Timed from the app's `guest_favourite.lottie` keyframes (44.955 fps). */
const LAURELS_PART = {
  delay: 0.445,
  duration: 0.556,
  ease: [0.91, 0, 0.18, 1],
} as const;
const LABEL_IN = {
  delay: 0.712,
  duration: 0.133,
  ease: [0.74, 0, 0.17, 1],
} as const;
/** The `gap-1` between each laurel and the label. */
const LABEL_GAP = 4;
/** In the Lottie the laurels start 28 canvas px apart: about 4px at the drawn 36px height. */
const START_GAP = 4;
/** Each laurel's start before the label is measured ("Guest Favorite" in bold 14px). */
const START_OFFSET_ESTIMATE = 37;

/**
 * The Guest Favourite card's laurels, as the app's Lottie: the laurels start almost
 * touching in the middle, part from 0.45s to 1.0s on a sharp ease, and the label fades in
 * between them from 0.71s, then stays. It plays once.
 */
const GuestFavouriteLaurels: React.FC<IGuestFavouriteLaurelsProps> = ({
  label,
  animated = false,
}) => {
  const reducedMotion = useReducedMotion();
  const play = animated && !reducedMotion;
  const labelRef = useRef<HTMLSpanElement>(null);

  const leftX = useMotionValue(play ? START_OFFSET_ESTIMATE : 0);
  const rightX = useMotionValue(play ? -START_OFFSET_ESTIMATE : 0);
  const labelOpacity = useMotionValue(play ? 0 : 1);

  useIsomorphicLayoutEffect(() => {
    if (!play) return;
    const labelWidth = labelRef.current?.offsetWidth ?? 0;
    const offset = (labelWidth + 2 * LABEL_GAP - START_GAP) / 2;
    leftX.set(offset);
    rightX.set(-offset);
    labelOpacity.set(0);
    const animations = [
      animate(leftX, 0, LAURELS_PART),
      animate(rightX, 0, LAURELS_PART),
      animate(labelOpacity, 1, LABEL_IN),
    ];
    return () => animations.forEach((animation) => animation.stop());
  }, [play, leftX, rightX, labelOpacity]);

  return (
    <span className="flex items-center justify-center gap-1 px-2">
      <motion.img
        src="/icons/listing/laurelleft.svg"
        alt=""
        className="h-9 w-auto"
        style={{ x: leftX }}
      />
      <motion.span
        ref={labelRef}
        className="text-center text-sm font-bold leading-4 text-ink"
        style={{ opacity: labelOpacity }}
      >
        {label}
      </motion.span>
      <motion.img
        src="/icons/listing/laurelright.svg"
        alt=""
        className="h-9 w-auto"
        style={{ x: rightX }}
      />
    </span>
  );
};

export default GuestFavouriteLaurels;
